import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api, { setCognitiveFlowsApiBase } from '../cognitive-flows/utils/api';
import { sanitizeClientName } from '../cognitive-flows/components/shared/mcpServerTools';
import Login from './login';

// The Worker's own guard (cux-mcp-server src/auth/authorize.ts) mints this id
// as a UUID, and the app's mcpConsentService.js re-validates it server-side
// with this exact pattern. Mirroring it here turns a 400 round trip into an
// immediate, clearer message instead of a wasted request.
const REQUEST_ID_RE = /^[A-Za-z0-9._-]{1,128}$/;

// Plain-language sentences for the scopes the Worker's /authorize can ask
// for (server/services/cognitive-flows/mcpServiceToken.js#MCP_SCOPES). An
// unrecognised scope string is intentionally NOT in this map — the fallback
// below renders it verbatim rather than hiding it, because a permission
// nobody can describe is exactly the one a person should see.
const SCOPE_SENTENCES = {
    'cf:read': 'See your agents, saved context, business files and data sources',
    'cf:context:write': 'Add and edit saved context',
    'cf:files:write': 'Add files to your memory (Knowledge Hub) and replace business files',
    'cf:workflow:write': 'Create and configure agents',
    'cf:workflow:run': "Run agents, which uses this store's credits",
};

// The only scope that spends money — gets visual emphasis on the card.
const CREDIT_SCOPE = 'cf:workflow:run';

const MALFORMED_LINK_INSTRUCTION = 'Start again from the app you are connecting.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// Both buttons wait for this one request before they render at all — even
// Deny. Without a bound, a hang here leaves the user unable to answer either
// way (fix round 1, item 4). On timeout or any other failure the `.catch`
// below falls through to the pre-picker behaviour: no picker, the session's
// store, the server's own checks decide.
const CONSENT_OPTIONS_TIMEOUT_MS = 15000;

// Our own sentences about the chosen store. None of them may carry the client
// name — it is the requester's to choose, and inside a sentence of ours it
// could forge one.
const MCP_NOT_INCLUDED_MESSAGE = "MCP isn't included for this store. Ask your account admin.";
// The product name on this page, in every appearance: connected AI apps know
// the MCP server as Almond (cux-mcp-server).
const BRAND_NAME = 'Almond';
const NO_WORKSPACE_MESSAGE = "You don't have access to any workspace in this store. Ask your account admin.";
const WORKSPACES_UNAVAILABLE_MESSAGE = "This store's workspaces couldn't be loaded. Reload the page to try again.";

// A row of GET /mcp-server/consent/options
// (server/services/cognitive-flows/mcpConsentRoutes.js#listConsentStores).
const isStoreRow = (row) => Boolean(row) && typeof row.shop === 'string' && row.shop !== '';

// `client` comes from the Worker's Dynamic Client Registration — self-service,
// attacker-controlled text, passed through unsanitised. It goes through the
// same sanitizer Settings' connected-app list uses (controls and bidi
// overrides stripped, capped at 60 characters) — one implementation, in
// shared/mcpServerTools.js, which explains why each rule is there.

/** Sends the browser to an EXTERNAL origin (the Worker), never a route in
 * this app's own router — react-router's navigate() cannot leave this
 * origin, so a real full-page redirect is required. Overridable so tests can
 * inject a spy instead of touching window.location, which jsdom will not let
 * a test set cleanly. */
function defaultPerformRedirect(url) {
    window.location.href = url;
}

/**
 * Is `value` a safe stashed return path? Pins the pathname to exactly
 * `/oauth/consent` while allowing the query string the Worker attaches
 * (`?req=&client=&scope=`) to survive the round trip through login. Rejects
 * an absolute URL and a protocol-relative one — an unvalidated stash that is
 * later handed to navigate() is an open redirect.
 */
function isSafeConsentReturnPath(value) {
    if (typeof value !== 'string' || !value) return false;
    if (!value.startsWith('/') || value.startsWith('//')) return false;
    const pathname = value.split(/[?#]/)[0];
    // React Router matches paths case-insensitively and tolerates a trailing
    // slash, so /OAuth/Consent/ would render ConsentGate (which stashes that
    // literal string) while a case-sensitive, no-trailing-slash compare here
    // would then refuse the very stash it just created — the same trap this
    // project already hit server-side with `/MCP-Server/…`
    // (mcpServicePrincipal.middleware.js). Normalize only for the comparison;
    // the caller still navigates to the ORIGINAL value, so a query string's
    // own casing (e.g. a client name) is never altered.
    const normalizedPathname = pathname.toLowerCase().replace(/\/+$/, '');
    return normalizedPathname === '/oauth/consent';
}

/**
 * Cookie-less visitor hitting /oauth/consent — stash the full path (query
 * intact) so login returns here, then show the normal login screen. Mirrors
 * ShareAcceptPage.jsx's ShareGate.
 */
export function ConsentGate({ app_url }) {
    const location = useLocation();
    useEffect(() => {
        sessionStorage.setItem('cfConsentReturn', `${location.pathname}${location.search}`);
    }, [location.pathname, location.search]);
    return <Login app_url={app_url} />;
}

/**
 * Mounted in the authed shell, beside ShareReturn. After a login initiated
 * from a consent link, bounce the user back to /oauth/consent with its query
 * intact. Renders nothing.
 */
export function ConsentReturn() {
    const navigate = useNavigate();
    useEffect(() => {
        const back = sessionStorage.getItem('cfConsentReturn');
        if (!back) return;
        // Clear unconditionally, BEFORE the validity check — a value that
        // fails validation must not linger for a later mount to find and
        // fail again silently.
        sessionStorage.removeItem('cfConsentReturn');
        if (isSafeConsentReturnPath(back)) {
            navigate(back, { replace: true });
        }
    }, [navigate]);
    return null;
}

const st = {
    // Rendered both inside the signed-in app shell (a 100vh grid next to the
    // sidebar) and on its own. It takes the grid's `main` area and scrolls
    // inside it: a centred card taller than the screen used to overflow above
    // the top (unreachable) and scroll the whole document, taking the sidebar
    // with it. Outside a grid, gridArea is ignored and max-height resolves to
    // none, so the page is one scrolling viewport. The card's auto margins
    // centre it when it fits and pin it to the top when it doesn't.
    page: { gridArea: 'main', height: '100vh', maxHeight: '100%', overflowY: 'auto', boxSizing: 'border-box', background: '#eef2f7', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, system-ui, sans-serif' },
    card: { maxWidth: '460px', width: '100%', margin: 'auto 0', background: '#fff', border: '1px solid #d3ddea', borderRadius: '16px', padding: '30px 28px', boxShadow: '0 4px 18px rgba(var(--cux-ink-rgb, 19, 40, 72), 0.10)', display: 'flex', flexDirection: 'column', gap: '14px' },
    brand: { fontSize: '13px', fontWeight: 800, color: 'var(--primary, var(--cux-ink, #132848))', letterSpacing: '0.02em' },
    provenance: { fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 },
    title: { fontSize: '19px', fontWeight: 800, color: 'var(--text-primary, #0f172a)', margin: 0, lineHeight: 1.4 },
    text: { fontSize: '13px', color: '#475569', margin: 0, lineHeight: 1.6 },
    scopeList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' },
    scopeItem: { fontSize: '13px', color: '#334155', padding: '8px 10px', borderRadius: '8px', background: '#f8fafc' },
    scopeCredit: { fontSize: '13px', color: '#7c2d12', padding: '8px 10px', borderRadius: '8px', background: '#fff7ed', border: '1px solid #fed7aa', fontWeight: 700 },
    // Multi-account choice (2026-09-24).
    modeList: { display: 'flex', flexDirection: 'column', gap: '10px', margin: 0, padding: 0, border: 'none' },
    modeCard: (on) => ({
        display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '12px', borderRadius: '10px', cursor: 'pointer',
        border: `1px solid ${on ? 'var(--primary, var(--cux-btn, #132848))' : '#D0D5DD'}`,
        background: on ? 'var(--cux-accent-light, #f5f8ff)' : '#fff',
    }),
    modeTitle: { fontSize: '14px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' },
    modeHint: { fontSize: '12.5px', color: '#475569', lineHeight: 1.5, marginTop: '2px' },
    recommended: {
        display: 'inline-block', marginLeft: '6px', padding: '1px 7px', borderRadius: '99px', fontSize: '11px', fontWeight: 700,
        background: 'var(--cux-utility-bg, #eff6ff)', color: 'var(--cux-utility-text, #1d4ed8)', verticalAlign: 'middle',
    },
    accountList: { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto', padding: '2px' },
    account: { border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 12px' },
    checkRow: { display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13.5px', color: '#334155', cursor: 'pointer' },
    nested: { display: 'flex', flexDirection: 'column', gap: '6px', margin: '8px 0 0 24px' },
    muted: { fontSize: '12px', color: '#94a3b8', marginLeft: '24px' },
    label: { fontSize: '13px', fontWeight: 600, color: '#334155' },
    select: { padding: '10px 12px', borderRadius: '8px', border: '1px solid #D0D5DD', fontSize: '14px' },
    actions: { display: 'flex', gap: '12px', marginTop: '6px' },
    denyBtn: { flex: 1, border: '1px solid #D0D5DD', background: '#fff', color: '#344054', borderRadius: '10px', padding: '10px 14px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    allowBtn: { flex: 1, border: 'none', background: 'var(--primary, var(--cux-btn, #132848))', color: '#fff', borderRadius: '10px', padding: '10px 14px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    error: { fontSize: '13px', color: '#b42318', margin: 0, background: '#fef3f2', border: '1px solid #fda29b', borderRadius: '8px', padding: '10px 12px' },
    notice: { fontSize: '13px', color: '#7c2d12', margin: 0, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '10px 12px' },
};

/**
 * The screen the MCP Worker's /authorize bounces the browser to: it cannot
 * read this app's session cookie, so this page authenticates the person (via
 * the surrounding App.js routing) and signs the decision on their behalf.
 *
 * The person picks which of their stores the app connects to (spec
 * 2026-09-21 §12.3). The list comes from the server — their own database
 * record, never the `stores` cookie — and so does the store shown: the server
 * binds only a store it has verified (mcpConsentRoutes.js), so this page never
 * names one from a cookie. "Entire account" is offered only where the server
 * says the connection could use it. If the list can't be loaded, the page
 * falls back to what it did before it could choose: no picker, the session's
 * store, unnamed, and the server's checks decide. No per-scope checkboxes in
 * v1 — see the plan doc.
 */
export default function OAuthConsentPage({ app_url, performRedirect = defaultPerformRedirect } = {}) {
    const [searchParams] = useSearchParams();
    const reqId = searchParams.get('req') || '';
    const clientName = useMemo(() => sanitizeClientName(searchParams.get('client')), [searchParams]);
    const scopeParam = searchParams.get('scope') || '';
    // Deduped — a repeated scope in the query string would otherwise render
    // as two list items sharing one React key.
    const scopes = useMemo(
        () => Array.from(new Set(scopeParam.split(/\s+/).filter(Boolean))),
        [scopeParam],
    );
    // An empty scope list is refused the same way a malformed req is: sending
    // it would only earn back the server's "That app asked for a permission
    // this store does not offer" — accurate for the server's own guard, but
    // misleading here where the real problem is "nothing was requested".
    const linkValid = REQUEST_ID_RE.test(reqId) && scopes.length > 0;

    // null while loading; [] when the list failed or named no store (the
    // fallback above); otherwise the stores to choose from.
    const [stores, setStores] = useState(null);
    const [selectedShop, setSelectedShop] = useState('');
    const [workspaces, setWorkspaces] = useState([]);
    const [workspacesStatus, setWorkspacesStatus] = useState('loading'); // loading | ready | failed
    const [workspaceId, setWorkspaceId] = useState('account');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [boundStoreName, setBoundStoreName] = useState('');
    // Multi-account consent — only when the server offers it (the API's
    // CF_MCP_MULTI_TARGET_CONSENT flag): "all" (every account and workspace,
    // now and later — the default) or "choose" (per account: all workspaces,
    // or the account level and chosen workspaces).
    const [multiTarget, setMultiTarget] = useState(false);
    const [allOffered, setAllOffered] = useState(false);
    const [mode, setMode] = useState('all');
    const [picked, setPicked] = useState({}); // shop → { all: boolean, entries: string[] }

    const choosing = Array.isArray(stores) && stores.length > 0;
    const selectedStore = choosing ? (stores.find((store) => store.shop === selectedShop) || null) : null;
    const mcpIncluded = !choosing || selectedStore?.mcpIncluded === true;
    // "Entire account" only where the server says the connection could use it.
    // In the fallback there is nothing to judge by, so it stays offered and the
    // server refuses it if it must.
    const offerAccount = !choosing || selectedStore?.accountLevel === true;
    const storeLabel = selectedStore ? (selectedStore.name || selectedStore.shop) : 'your store';
    // The picker must never offer a workspace the resulting grant cannot use
    // (fix round 1, item 1; spec §12.3): `workspaceIds` is an array only when
    // the chosen store's row is restricted (a staff row, or a member granted
    // specific workspaces) — `null`/absent means no restriction, so every
    // workspace `/workspaces?shop=` lists stays visible.
    const workspaceIdFilter = Array.isArray(selectedStore?.workspaceIds) ? new Set(selectedStore.workspaceIds) : null;
    const visibleWorkspaces = workspaceIdFilter
        ? workspaces.filter((ws) => workspaceIdFilter.has(ws.id))
        : workspaces;

    useEffect(() => {
        // The shared CF axios instance defaults to the relative
        // '/api/cognitive-flows', which only resolves via CRA's dev proxy on
        // localhost. Every other top-level page that uses this instance
        // outside the /cognitive-flows/* layout sets the real base first
        // (ShareAcceptPage.jsx, CanvasSharePage.jsx, ...) — without this, a
        // dev/prod POST here 404s or 405s silently and Allow never redirects.
        if (app_url) setCognitiveFlowsApiBase(app_url);
        if (!linkValid) return undefined;
        let cancelled = false;
        api.get('/mcp-server/consent/options', { timeout: CONSENT_OPTIONS_TIMEOUT_MS })
            .then(({ data }) => {
                if (cancelled) return;
                const list = Array.isArray(data?.stores) ? data.stores.filter(isStoreRow) : [];
                const initial = list.find((store) => store.current) || list[0];
                if (initial) {
                    setSelectedShop(initial.shop);
                    setWorkspaceId(initial.accountLevel === true ? 'account' : '');
                }
                if (data?.multiTarget === true) {
                    const offered = data?.allAccounts?.offered === true && list.some((store) => store.mcpIncluded);
                    setMultiTarget(true);
                    setAllOffered(offered);
                    setMode(offered ? 'all' : 'choose');
                    const start = list.find((store) => store.current && store.mcpIncluded) || list.find((store) => store.mcpIncluded);
                    setPicked(start ? { [start.shop]: { all: true, entries: [] } } : {});
                }
                setStores(list);
            })
            .catch(() => {
                if (!cancelled) setStores([]);
            });
        return () => { cancelled = true; };
    }, [linkValid, app_url]);

    // The workspaces of the chosen store — or, in the fallback, of the
    // session's store. A store without MCP offers none: nothing can be granted.
    useEffect(() => {
        if (!linkValid || stores === null) return undefined;
        if (choosing && !mcpIncluded) return undefined;
        let cancelled = false;
        setWorkspaces([]);
        setWorkspacesStatus('loading');
        const request = choosing
            ? api.get('/workspaces', { params: { shop: selectedShop } })
            : api.get('/workspaces');
        request
            .then(({ data }) => {
                if (cancelled) return;
                setWorkspaces(Array.isArray(data?.workspaces) ? data.workspaces : []);
                setWorkspacesStatus('ready');
            })
            .catch(() => {
                // In the fallback the grant still works without the picker
                // populated (account level); when choosing, the page says so.
                if (!cancelled) setWorkspacesStatus('failed');
            });
        return () => { cancelled = true; };
    }, [linkValid, stores, choosing, selectedShop, mcpIncluded]);

    const handleStoreChange = (shop) => {
        const next = stores.find((store) => store.shop === shop);
        setSelectedShop(shop);
        // A workspace belongs to one store: the choice starts over.
        setWorkspaceId(next?.accountLevel === true ? 'account' : '');
        setErrorMessage('');
    };

    const pickedShops = Object.keys(picked);
    const coverageFor = () => (mode === 'all'
        ? { all: true }
        : { targets: pickedShops.map((shop) => ({ shop, workspaces: picked[shop].all ? 'all' : picked[shop].entries })) });
    const multiReady = mode === 'all'
        ? allOffered
        : pickedShops.length > 0 && pickedShops.every((shop) => picked[shop].all || picked[shop].entries.length > 0);

    const handleDecision = (decision) => {
        setSubmitting(true);
        setErrorMessage('');
        const body = { req: reqId, decision, scopes, workspaceId };
        if (selectedStore) body.shop = selectedStore.shop;
        if (multiTarget && decision === 'approve') {
            body.coverage = coverageFor();
            delete body.workspaceId;
            delete body.shop;
        }
        api.post('/mcp-server/consent', body)
            .then(({ data }) => {
                // A 200 with no redirect (e.g. an SPA fallback quietly
                // serving index.html for a path that 404s) must not hand the
                // browser the literal string "undefined" to navigate to.
                if (!data?.redirect) {
                    setErrorMessage(GENERIC_ERROR_MESSAGE);
                    setSubmitting(false);
                    return;
                }
                // The store(s) the server actually signed for.
                if (decision === 'approve' && data.coverage?.all === true) setBoundStoreName('all your accounts');
                else if (decision === 'approve' && Array.isArray(data.coverage?.accounts)) {
                    const n = data.coverage.accounts.length;
                    setBoundStoreName(n === 1 ? (data.coverage.accounts[0].name || '1 account') : `${n} accounts`);
                } else if (decision === 'approve' && typeof data.store?.name === 'string') setBoundStoreName(data.store.name);
                performRedirect(data.redirect);
            })
            .catch((err) => {
                setErrorMessage(err?.response?.data?.error || GENERIC_ERROR_MESSAGE);
                setSubmitting(false);
            });
    };

    if (!linkValid) {
        return (
            <div style={st.page}>
                <div style={st.card}>
                    <span style={st.brand}>{BRAND_NAME}</span>
                    <h1 style={st.title}>This authorization link is malformed</h1>
                    <p style={st.text}>{MALFORMED_LINK_INSTRUCTION}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={st.page}>
            <div style={st.card}>
                <span style={st.brand}>{BRAND_NAME}</span>
                {/* The client name below is attacker-controlled (self-service
                    Dynamic Client Registration on the Worker) and could be
                    registered as e.g. "Almond" to imitate this brand
                    chip. This line makes the trust boundary explicit however
                    the name reads. */}
                <p style={st.provenance}>An external app is requesting access — not part of this dashboard</p>
                <h1 style={st.title}>
                    {multiTarget
                        ? <><strong>{clientName}</strong> wants to connect to your {BRAND_NAME} accounts</>
                        : <><strong>{clientName}</strong> wants to connect to <strong>{storeLabel}</strong></>}
                </h1>
                <p style={st.text}>This will let {clientName} do the following:</p>
                <ul style={st.scopeList}>
                    {scopes.map((scope) => (
                        <li key={scope} style={scope === CREDIT_SCOPE ? st.scopeCredit : st.scopeItem}>
                            {SCOPE_SENTENCES[scope] || scope}
                            {scope === CREDIT_SCOPE && selectedStore && !multiTarget ? ` (${storeLabel})` : ''}
                            {scope === CREDIT_SCOPE && multiTarget ? ' (in the account each run is in)' : ''}
                        </li>
                    ))}
                </ul>

                {/* The buttons wait for the store list: the store they act on
                    is not known until it arrives. */}
                {stores === null ? (
                    <p style={st.text}>Loading your stores…</p>
                ) : (
                    multiTarget ? (
                    <>
                        <MultiTargetChoice
                            stores={stores}
                            allOffered={allOffered}
                            mode={mode}
                            onMode={(m) => { setMode(m); setErrorMessage(''); }}
                            picked={picked}
                            onPicked={(next) => { setPicked(next); setErrorMessage(''); }}
                            disabled={submitting}
                        />
                        {errorMessage && <p role="alert" style={st.error}>{errorMessage}</p>}
                        {boundStoreName && (
                            <p role="status" style={st.text}>Allowed for {boundStoreName}. Returning you to the app…</p>
                        )}
                        <div style={st.actions}>
                            <button type="button" disabled={submitting} onClick={() => handleDecision('deny')} style={st.denyBtn}>
                                Deny
                            </button>
                            <button
                                type="button"
                                disabled={submitting || !multiReady}
                                onClick={() => handleDecision('approve')}
                                style={st.allowBtn}
                            >
                                Allow
                            </button>
                        </div>
                    </>
                    ) : (
                    <>
                        {stores.length > 1 && (
                            <>
                                <label style={st.label} htmlFor="oauth-consent-store">Store</label>
                                <select
                                    id="oauth-consent-store"
                                    value={selectedShop}
                                    onChange={(e) => handleStoreChange(e.target.value)}
                                    disabled={submitting}
                                    style={st.select}
                                >
                                    {stores.map((store) => (
                                        <option key={store.shop} value={store.shop}>{store.name || store.shop}</option>
                                    ))}
                                </select>
                            </>
                        )}

                        {!mcpIncluded ? (
                            <p role="note" style={st.notice}>{MCP_NOT_INCLUDED_MESSAGE}</p>
                        ) : (
                            <WorkspaceChoice
                                choosing={choosing}
                                offerAccount={offerAccount}
                                workspaces={visibleWorkspaces}
                                status={workspacesStatus}
                                value={workspaceId}
                                onChange={setWorkspaceId}
                            />
                        )}

                        {errorMessage && <p role="alert" style={st.error}>{errorMessage}</p>}
                        {boundStoreName && (
                            <p role="status" style={st.text}>Allowed for {boundStoreName}. Returning you to the app…</p>
                        )}

                        <div style={st.actions}>
                            <button type="button" disabled={submitting} onClick={() => handleDecision('deny')} style={st.denyBtn}>
                                Deny
                            </button>
                            <button
                                type="button"
                                disabled={submitting || !mcpIncluded || workspaceId === ''}
                                onClick={() => handleDecision('approve')}
                                style={st.allowBtn}
                            >
                                Allow
                            </button>
                        </div>
                    </>
                    )
                )}
            </div>
        </div>
    );
}

/**
 * Multi-account consent: "All accounts and all workspaces" (recommended, the
 * default — covers accounts and workspaces added later), or a per-account
 * checklist where each checked account covers all its workspaces (including
 * new ones) unless narrowed to the account level and chosen workspaces. Only
 * accounts that include MCP can be chosen; the server re-checks everything.
 */
function MultiTargetChoice({ stores, allOffered, mode, onMode, picked, onPicked, disabled }) {
    const toggleStore = (shop) => {
        const next = { ...picked };
        if (next[shop]) delete next[shop];
        else next[shop] = { all: true, entries: [] };
        onPicked(next);
    };
    const toggleAll = (shop) => {
        const cur = picked[shop];
        onPicked({ ...picked, [shop]: { all: !cur.all, entries: cur.all ? [] : cur.entries } });
    };
    const toggleEntry = (shop, entry) => {
        const cur = picked[shop];
        const entries = cur.entries.includes(entry) ? cur.entries.filter((e) => e !== entry) : [...cur.entries, entry];
        onPicked({ ...picked, [shop]: { all: false, entries } });
    };
    return (
        <fieldset style={st.modeList} disabled={disabled}>
            <legend style={{ ...st.label, marginBottom: '6px' }}>What can this app reach?</legend>
            {allOffered && (
                <label style={st.modeCard(mode === 'all')} data-testid="oauth-consent-mode-all">
                    <input type="radio" name="oauth-consent-mode" checked={mode === 'all'} onChange={() => onMode('all')} style={{ marginTop: '3px' }} />
                    <span>
                        <span style={st.modeTitle}>All accounts and all workspaces</span>
                        <span style={st.recommended}>Recommended</span>
                        <span style={{ ...st.modeHint, display: 'block' }}>
                            Includes accounts and workspaces you&apos;re added to later — no need to reconnect. The app asks which
                            account and workspace to use before it changes anything.
                        </span>
                    </span>
                </label>
            )}
            <label style={st.modeCard(mode === 'choose')} data-testid="oauth-consent-mode-choose">
                <input type="radio" name="oauth-consent-mode" checked={mode === 'choose'} onChange={() => onMode('choose')} style={{ marginTop: '3px' }} />
                <span>
                    <span style={st.modeTitle}>Choose accounts and workspaces</span>
                    <span style={{ ...st.modeHint, display: 'block' }}>Only the accounts and workspaces you tick below.</span>
                </span>
            </label>
            {mode === 'choose' && (
                <div style={st.accountList} role="group" aria-label="Accounts">
                    {stores.map((store) => {
                        const sel = picked[store.shop];
                        const name = store.name || store.shop;
                        return (
                            <div key={store.shop} style={st.account}>
                                <label style={st.checkRow}>
                                    <input type="checkbox" checked={!!sel} disabled={!store.mcpIncluded} onChange={() => toggleStore(store.shop)} aria-label={name} />
                                    <strong>{name}</strong>
                                </label>
                                {!store.mcpIncluded && <div style={st.muted}>{MCP_NOT_INCLUDED_MESSAGE}</div>}
                                {sel && (
                                    <div style={st.nested}>
                                        <label style={st.checkRow}>
                                            <input type="checkbox" checked={sel.all} onChange={() => toggleAll(store.shop)} />
                                            All workspaces in this account (including new ones)
                                        </label>
                                        {!sel.all && (
                                            <>
                                                {store.accountLevel && (
                                                    <label style={st.checkRow}>
                                                        <input type="checkbox" checked={sel.entries.includes('account')} onChange={() => toggleEntry(store.shop, 'account')} />
                                                        Entire account
                                                    </label>
                                                )}
                                                {(store.workspaces || []).map((ws) => (
                                                    <label key={ws.id} style={st.checkRow}>
                                                        <input type="checkbox" checked={sel.entries.includes(ws.id)} onChange={() => toggleEntry(store.shop, ws.id)} />
                                                        {ws.name}
                                                    </label>
                                                ))}
                                                {!store.accountLevel && !(store.workspaces || []).length && <div style={st.muted}>{NO_WORKSPACE_MESSAGE}</div>}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </fieldset>
    );
}

/**
 * "Grant access to": the chosen store's workspaces, with "Entire account"
 * first when it is offered. With nothing to offer — no account level and no
 * workspace — it says why instead of showing an empty picker.
 */
function WorkspaceChoice({ choosing, offerAccount, workspaces, status, value, onChange }) {
    if (choosing && !offerAccount && status === 'failed') {
        return <p role="note" style={st.notice}>{WORKSPACES_UNAVAILABLE_MESSAGE}</p>;
    }
    if (choosing && !offerAccount && status === 'ready' && workspaces.length === 0) {
        return <p role="note" style={st.notice}>{NO_WORKSPACE_MESSAGE}</p>;
    }
    return (
        <>
            <label style={st.label} htmlFor="oauth-consent-workspace">Grant access to</label>
            <select
                id="oauth-consent-workspace"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={st.select}
            >
                {offerAccount
                    ? <option value="account">Entire account</option>
                    : <option value="" disabled>Choose a workspace</option>}
                {workspaces.map((ws) => (
                    <option key={ws.id} value={ws.id}>{ws.name}</option>
                ))}
            </select>
            {choosing && status === 'failed' && <p role="note" style={st.notice}>{WORKSPACES_UNAVAILABLE_MESSAGE}</p>}
        </>
    );
}
